/**
 * COMMERCE-176B-R2-R2 — Home promo strip (lower Home slot). Catalog UAH prices only.
 */
import { useEffect, useMemo, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { formatUah } from "./money-v2.js";
import { ensurePromoPriceOverlayLoaded } from "./promo-price-overlay-v2.js";

type PromoItem = {
  product_id: string;
  title: string;
  image: string | null;
  brand: string | null;
  price_uah: number;
  old_price_uah?: number;
  availability?: string;
  promotion_source?: string;
};

type Props = {
  lang: StoreV2Lang;
  sessionKey: string;
  onOpenProduct: (
    productId: string,
    seed?: {
      id: string;
      title?: string;
      imageUrl?: string | null;
      priceUah?: number | null;
    },
  ) => void;
  onOpenAllPromotions: () => void;
};

const CANARY_EXCLUDE_IDS = new Set(["3053502735"]);

function sessionSeed(sessionKey: string): number {
  let h = 0;
  for (let i = 0; i < sessionKey.length; i += 1) h = (h * 31 + sessionKey.charCodeAt(i)) >>> 0;
  return h || 1;
}

function pickStableCount(seed: number, min: number, max: number): number {
  const span = max - min + 1;
  return min + (seed % span);
}

function pickStable(items: PromoItem[], seed: number, n: number): PromoItem[] {
  if (items.length <= n) return items;
  const copy = [...items];
  let s = seed;
  for (let i = copy.length - 1; i > 0; i -= 1) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const j = s % (i + 1);
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy.slice(0, n);
}

function isPromoEligible(item: PromoItem): boolean {
  if (!item.product_id || CANARY_EXCLUDE_IDS.has(item.product_id)) return false;
  if (!(Number(item.price_uah) >= 1)) return false;
  const availability = String(item.availability ?? "in_stock").toLowerCase();
  if (availability === "out_of_stock") return false;
  return true;
}

function discountPct(price: number, oldPrice?: number): number | null {
  if (oldPrice == null || !(oldPrice > price) || price <= 0) return null;
  return Math.round(((oldPrice - price) / oldPrice) * 100);
}

export function HomePromoStripV2({ lang, sessionKey, onOpenProduct, onOpenAllPromotions }: Props) {
  const [items, setItems] = useState<PromoItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    ensurePromoPriceOverlayLoaded();
    void fetch(`/data/promotions-v1.json?v=${encodeURIComponent(sessionKey.slice(0, 8))}`, {
      cache: "force-cache",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((doc) => {
        if (cancelled || !doc) return;
        const list = Array.isArray(doc.products) ? (doc.products as PromoItem[]) : [];
        setItems(list.filter(isPromoEligible));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  const picked = useMemo(() => {
    if (!items?.length) return [];
    const seed = sessionSeed(sessionKey);
    const count = pickStableCount(seed, 4, 6);
    return pickStable(items, seed ^ 0x9e3779b9, count);
  }, [items, sessionKey]);

  if (!picked.length) return null;

  return (
    <section className="sv2-home-promo-strip" data-testid="sv2-home-promo-strip" data-home-slot="lower">
      <h2>{lang === "en" ? "🔥 Deals & offers" : "🔥 Акції та вигідні пропозиції"}</h2>
      <div className="sv2-home-promo-scroll">
        {picked.map((p) => {
          const pct = discountPct(p.price_uah, p.old_price_uah);
          return (
            <button
              key={p.product_id}
              type="button"
              className="sv2-home-promo-card"
              data-testid="sv2-home-promo-card"
              onClick={() =>
                onOpenProduct(p.product_id, {
                  id: p.product_id,
                  title: p.title,
                  imageUrl: p.image,
                  priceUah: p.price_uah,
                })
              }
            >
              {p.image ? <img src={p.image} alt="" loading="lazy" /> : <div style={{ height: 96 }} />}
              <strong>{p.title}</strong>
              <span style={{ fontSize: 13, fontWeight: 800 }}>
                {formatUah(p.price_uah)}
                {typeof p.old_price_uah === "number" && p.old_price_uah > p.price_uah ? (
                  <span
                    className="sv2-muted"
                    style={{ marginLeft: 6, textDecoration: "line-through", fontWeight: 600 }}
                  >
                    {formatUah(p.old_price_uah)}
                  </span>
                ) : null}
                {pct != null && pct > 0 ? (
                  <span className="sv2-promo-badge" style={{ marginLeft: 6 }}>
                    −{pct}%
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="sv2-home-promo-see-all"
        data-testid="sv2-home-promo-see-all"
        onClick={onOpenAllPromotions}
      >
        {lang === "en" ? "View all deals" : "Переглянути всі акції"}
      </button>
    </section>
  );
}
